import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useWorkListActions, useWorkListState } from '../../shared/hooks/useApi/useWorkList';
import { WorkData } from '../../shared/dto/EntityRepository';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useBulkStore, useWorkListStore } from '../../shared/store/WorkListStore';
import { useUnsavedChanges } from '../../shared/hooks/useUnsavedChanges';
import WorkInfo from './WorkInfo';
import EmptyWorkList from '../work/EmptyWorkList';
import DragDropLayer from '../work/DragDropLayer';
import WorkViewer from './WorkViewer';
import WorkViewerInfo from './WorkViewerInfo';
import MultiWorkViewer from './MultiWorkViewer';
import WorkViewerShell from './WorkViewerShell';
import IndexWorkViewer from './IndexWorkViewer';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { BULK_UPLOAD_IMAGE_MAX_COUNT } from '../../shared/constants/limits';
import { AlertPosition, AlertType, ModalType, perPageToNumber, WorkListType, getNextWorkListType } from '../../shared/enum/EnumRepository';
import { useModalStore } from '../../shared/store/portal/modalStore';
import HintWrapper from '../../shared/component/headless/button/HintWrapper';
import { getHintMessage } from '../../shared/constants/hintMessages';
import createIcon from '../../asset/icon/create.png';
import { useBulk } from '../../shared/hooks/useApi/useBulk';
import { useWorkDetailActions } from '../../shared/hooks/useApi/useWorkDetail';
import { getImageDimensions, removeFileExtension } from '../../shared/util/imageUtils';
import { filterSupportedImageFiles } from '../../shared/util/imageFileFormat';
import { UpdateImgReq } from '../../shared/dto/ReqDtoRepository';
import { useWorkListSelection } from '../../shared/hooks/useWorkListSelection';
import { useWorkIndexing } from '../../shared/hooks/useWorkIndexing';
import { useWorkTagStore } from '../../shared/store/workTagStore';
import { withWorkLabelFilter } from '../work/buildWorkListParams';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import downloadIcon from '../../asset/icon/download.png';
import gridIcon from '../../asset/icon/grid.png';
import listIcon from '../../asset/icon/list.png';
import { useWorkListMarquee } from '../../shared/hooks/useWorkListMarquee';
import reorderIcon from '../../asset/icon/reorder.png';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { useParams } from 'react-router-dom';
import { isPageOwnerMatchedToRaw } from '../../shared/util/pageOwnerMatch';
import { canShowWorkList } from '../../shared/util/visibility/workListVisibility';
import type { ResourceScope } from '../../shared/util/scopedResource';
import { useWorkPropertyVisible } from '../../shared/hooks/useApi/useWorkPropertyVisible';
import PageAboutWork from './PageAboutWork';
import WorkMetaStation from './WorkMetaStation';
import { usePageStore } from '../../shared/store/pageStore';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import { useImageFileDropState } from '../../shared/hooks/useImageFileDropState';


const CREATE_WORK_INPUT_ID = 'works-create-work-input';

function workGridBasisForPackedWidth(width: number, cols: number) {
  return (252 * width + 300 * (cols + 5)) / (47 * cols - 17);
}

function sideGridHeightRangeVh(isEditMode: boolean, hasWorkSelected: boolean, isIndexingMode: boolean) {
  if (isIndexingMode) return { minVh: 50, maxVh: 50 };
  if (isEditMode) return { minVh: 30, maxVh: 30 };
  if (hasWorkSelected) return { minVh: 30, maxVh: 50 };
  return { minVh: 30, maxVh: 60 };
}

const WorkList: React.FC = () => {
  const { isOwner } = useAuth();
  const { isEditMode } = useEditMode();
  const { workList, workListType, listOwnerAui, listScope, status } = useWorkListState();
  const { getWorkList, getWorkListOwner, updateWorkBulk, updateWorkListType } = useWorkListActions();
  const { createWorkBulk, createDetailBulk } = useBulk();
  const { updateWorkDetailBulk } = useWorkDetailActions();
  const pageSize = useWorkListStore((s) => s.pageSize);
  const sortData = useWorkListStore((s) => s.sortData);
  const filterData = useWorkListStore((s) => s.filterData);
  const page = usePageStore((s) => s.page);
  const { runAfterUnsavedChangesCheck } = useUnsavedChanges();
  const { enqueueAlert } = useStandardAlertStore();
  const {
    activeDropTarget: dragState,
    handleDropTargetDragEnter,
    handleFileDragOver,
    handleFileDragLeave,
    handleImageFileDrop,
  } = useImageFileDropState<'list' | 'viewer'>();
  const [listHeightPx, setListHeightPx] = useState<number | null>(null);
  const [listHeightReady, setListHeightReady] = useState(false);
  const workListRef = useRef<HTMLDivElement>(null);
  const { setStandardModal } = useModalStore();
  const { getWorkPropertyVisible } = useWorkPropertyVisible();
  const hasMultiSelected = useWorkViewStore((s) => s.multiSelectedWork.length > 0);
  const isOverviewOpen = useWorkViewStore((s) => s.isOverviewOpen);
  const activeWorkId = useWorkViewStore((s) => s.activeWork?.id);
  const { setTotal, clearCurrent } = useBulkStore();
  const selectedTags = useWorkTagStore((s) => s.selectedTags);
  const untaggedSelected = useWorkTagStore((s) => s.untaggedSelected);
  const { isIndexingMode, setIsIndexingMode } = useWorkIndexing();
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const { HANDLE: raw } = useParams<{ HANDLE?: string }>();
  const currentScope: ResourceScope = isOwner ? 'owner' : 'public';
  const canShowList = canShowWorkList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    listScope,
    currentScope,
    status,
  });
  const visibleWorkList = canShowList ? workList : [];
  const gridCols = 4;
  const isOverviewOpenWithIndexing = isOverviewOpen || isIndexingMode;
  const hasWorkSelected = isOverviewOpen || hasMultiSelected;
  const { minVh: sideGridMinVh, maxVh: sideGridMaxVh } = sideGridHeightRangeVh(isEditMode, hasWorkSelected, isIndexingMode);
  const { handleWorkClick, handleKeyDown, commitMarqueeHitWorksPreview, handleWorkListBackgroundClick } =
    useWorkListSelection(visibleWorkList, { gridCols });

  const sideGridHostRef = useRef<HTMLDivElement>(null);
  const sideGridRef = useRef<HTMLDivElement>(null);
  const listHeightRafRef = useRef(0);
  const isFirstListHeightRef = useRef(true);

  const marqueeVisualEnabled = isEditMode;
  const {
    handleWorkListMouseDown,
    handleWorkListClickCapture,
    marqueeBox,
    marqueeSelecting,
  } = useWorkListMarquee({
    workList: visibleWorkList,
    workListCompRef: sideGridHostRef,
    commitMarqueeHitWorksPreview,
    enabled: marqueeVisualEnabled,
  });

  const handleDownloadPDF = () => {
    setStandardModal({
      modalType: ModalType.CREATE_WORK_PDF,
      title: null,
      value: null,
      handleChange: () => {},
    });
  };

  useEffect(() => {
    if (workListRef.current) {
      workListRef.current.focus();
    }
  }, [workListRef]);

  useEffect(() => {
    if (isEditMode || !activeWorkId) return;
    const host = sideGridHostRef.current;
    if (!host) return;
    const el = host.querySelector(`[data-work-id="${CSS.escape(String(activeWorkId))}"]`);
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeWorkId, isEditMode]);

  useLayoutEffect(() => {
    const side = sideGridRef.current;
    const host = sideGridHostRef.current;
    if (!side || !host) return;

    const applySideBasis = () => {
      const hostStyle = getComputedStyle(host);
      const available =
        host.clientWidth
        - (parseFloat(hostStyle.paddingLeft) || 0)
        - (parseFloat(hostStyle.paddingRight) || 0);
      if (available <= 0) return;
      const basis = workGridBasisForPackedWidth(available, gridCols);
      side.style.setProperty('--work-grid-basis', `${basis}px`);
    };

    applySideBasis();
    const observer = new ResizeObserver(applySideBasis);
    observer.observe(host);
    return () => observer.disconnect();
  }, [gridCols, visibleWorkList.length]);

  useLayoutEffect(() => {
    if (visibleWorkList.length === 0) {
      isFirstListHeightRef.current = true;
      setListHeightPx(null);
      setListHeightReady(true);
      return;
    }
    const inner = sideGridRef.current;
    if (!inner) return;

    const applyListHeight = () => {
      const contentH = inner.getBoundingClientRect().height;
      const vh = window.innerHeight;
      const minH = Math.round(vh * (sideGridMinVh / 100));
      const maxH = Math.round(vh * (sideGridMaxVh / 100));
      const next = Math.round(Math.min(maxH, Math.max(minH, contentH)));
      if (isFirstListHeightRef.current) {
        isFirstListHeightRef.current = false;
        setListHeightPx(next);
        return;
      }
      cancelAnimationFrame(listHeightRafRef.current);
      listHeightRafRef.current = requestAnimationFrame(() => {
        setListHeightPx((prev) => (prev === next ? prev : next));
      });
    };

    applyListHeight();
    const observer = new ResizeObserver(applyListHeight);
    observer.observe(inner);
    window.addEventListener('resize', applyListHeight);
    const readyId = requestAnimationFrame(() => setListHeightReady(true));

    return () => {
      cancelAnimationFrame(readyId);
      cancelAnimationFrame(listHeightRafRef.current);
      observer.disconnect();
      window.removeEventListener('resize', applyListHeight);
    };
  }, [visibleWorkList.length, sideGridMinVh, sideGridMaxVh]);

  useEffect(() => {
    if (!isPageOwnerMatchedToRaw(raw, pageOwner)) return;
    void getWorkPropertyVisible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, pageOwner?.aui]);

  useEffect(() => {
    const getWorkListWithApi = async () => {
      if (!isPageOwnerMatchedToRaw(raw, pageOwner)) return;
      if (isOwner) {
        await getWorkListOwner({ page: 1, size: pageSize, sortData: sortData, filterData: withWorkLabelFilter(filterData, selectedTags, untaggedSelected, isOwner) });
      } else {
        await getWorkList({ page: 1, size: pageSize, sortData: sortData, filterData: withWorkLabelFilter(filterData, selectedTags, untaggedSelected, isOwner) });
      }
    }
    getWorkListWithApi();
  }, [raw, pageOwner?.aui, pageSize, sortData, filterData, isOwner, selectedTags, untaggedSelected]);

  const openBulkCreateModal = (
    imageFiles: File[],
    target: 'list' | 'viewer',
  ) => {
    if (imageFiles.length > BULK_UPLOAD_IMAGE_MAX_COUNT) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('BULK_UPLOAD_IMAGE_MAX_EXCEEDED', { maxCount: BULK_UPLOAD_IMAGE_MAX_COUNT })
      });
      return;
    }
    if (imageFiles.length > 0) {
      setTotal(imageFiles.length);
      clearCurrent();
      setStandardModal({
        modalType: ModalType.BULK_UPLOAD,
        title: target === 'list' ? 'Works' : `Detail Cuts`,
        value: target,
        data: imageFiles,
        handleChange: () => { }
      });
    }
  };

  const handleCreateWork = () => {
    document.getElementById(CREATE_WORK_INPUT_ID)?.click();
  };

  const createSingleWorkFromFile = async (file: File) => {
    setTotal(1);
    clearCurrent();
    const results = await createWorkBulk([file]);
    if (!results?.length) return;
    const { width, height } = await getImageDimensions(file);
    const workListPayload: UpdateImgReq[] = [{
      id: results[0].workId,
      originUrl: results[0].originUrl,
      width,
      height,
      title: removeFileExtension(file.name)
    }];
    await updateWorkBulk({ workList: workListPayload });
    await getWorkListOwner({ page: 1, size: pageSize, sortData, filterData: withWorkLabelFilter(filterData, selectedTags, untaggedSelected, isOwner) });
  };

  const createSingleDetailFromFile = async (file: File) => {
    const activeWork = useWorkViewStore.getState().activeWork;
    if (!activeWork) return;
    const results = await createDetailBulk([file], activeWork.id);
    if (!results?.length) return;
    const { width, height } = await getImageDimensions(file);
    await updateWorkDetailBulk({
      detailList: [{
        id: results[0].workDetailId,
        originUrl: results[0].originUrl,
        width,
        height,
        description: removeFileExtension(file.name),
      }],
    });
  };

  const handleCreateWorkFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = filterSupportedImageFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
    if (files.length === 0) return;

    if (files.length > BULK_UPLOAD_IMAGE_MAX_COUNT) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('BULK_UPLOAD_IMAGE_MAX_EXCEEDED', { maxCount: BULK_UPLOAD_IMAGE_MAX_COUNT })
      });
      return;
    }

    if (files.length === 1) {
      const [file] = files;
      void createSingleWorkFromFile(file);
      return;
    }

    setTotal(files.length);
    clearCurrent();
    setStandardModal({
      modalType: ModalType.BULK_UPLOAD,
      title: 'Works',
      value: 'list',
      data: files,
      handleChange: () => { }
    });
  };

  const handleListDragEnter = (e: React.DragEvent) => {
    handleDropTargetDragEnter(e, 'list', isEditMode && !isIndexingMode);
  };

  const handleViewerDragEnter = (e: React.DragEvent) => {
    handleDropTargetDragEnter(
      e,
      'viewer',
      isEditMode && !isIndexingMode && !hasMultiSelected,
    );
  };

  /** Files 드래그는 upload(copy), 워크 행 같은 HTML 드래그는 move 필요 (!= copy 이어야 drop 허용) */
  const handleDragOver = (e: React.DragEvent, fileDropEnabled: boolean) => {
    if (handleFileDragOver(e, fileDropEnabled)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDroppedImages = (
    imageFiles: File[],
    target: 'list' | 'viewer',
  ) => {
    if (target === 'viewer' && !useWorkViewStore.getState().activeWork) return;
    if (imageFiles.length === 1) {
      const [file] = imageFiles;
      if (target === 'viewer') {
        void createSingleDetailFromFile(file);
      } else {
        void createSingleWorkFromFile(file);
      }
    } else {
      openBulkCreateModal(imageFiles, target);
    }
  };

  const handleDrop = (
    e: React.DragEvent,
    target: 'list' | 'viewer',
  ) => {
    const enabled = (
      isEditMode &&
      !isIndexingMode &&
      (target === 'list' || !hasMultiSelected)
    );
    handleImageFileDrop(e, target, enabled, handleDroppedImages);
  };

  const handleContainerDragLeave = (e: React.DragEvent) => {
    handleFileDragLeave(e);
  };

  const handleReorderWorkList = () => {
    if (!isEditMode) return;
    if (isIndexingMode) {
      runAfterUnsavedChangesCheck(() => setIsIndexingMode(false));
      return;
    }
    setIsIndexingMode(true);
  };

  const handleChangeWorkView = async () => {
    if (isIndexingMode) return;
    await updateWorkListType({
      workListType: getNextWorkListType(workListType),
    });
  };

  const editButtons = isEditMode && (
    <BtnContainer data-suppress-marquee>
      {!isIndexingMode && (
        <>
          <HiddenFileInput
            id={CREATE_WORK_INPUT_ID}
            type="file"
            accept="image/*"
            multiple
            onChange={handleCreateWorkFileChange}
          />
          <HintWrapper
            content={getHintMessage('CREATE_WORK')}
            content2={getHintMessage('CREATE_WORK_2')}
            toLeft={true}>
            <UniversalIconBtn
              size="standard"
              border={true}
              backgroundImg={createIcon}
              onClick={handleCreateWork} />
          </HintWrapper>
          <HintWrapper
            content={getHintMessage('CHANGE_WORK_VIEW')}
            toLeft={true}>
            <UniversalIconBtn
              size="standard"
              border={true}
              backgroundImg={workListType === WorkListType.GRID ? listIcon : gridIcon}
              onClick={handleChangeWorkView}
            />
          </HintWrapper>
        </>
      )}
      <HintWrapper 
        content={getHintMessage('REORDER_WORK_LIST')}
        toLeft={true}>
        <UniversalIconBtn
          size="standard"
          border={true}
          backgroundImg={reorderIcon}
          onClick={handleReorderWorkList}
        />
      </HintWrapper>
      <HintWrapper
        content={getHintMessage('DOWNLOAD_WORK_LIST_PDF')}
        toLeft={true}>
        <UniversalIconBtn
          size="standard"
          border={false}
          backgroundImg={downloadIcon}
          onClick={handleDownloadPDF}
        />
      </HintWrapper>
    </BtnContainer>
  );

  return (
    <WorkListContainer
      tabIndex={0}
      ref={workListRef}
      onKeyDown={handleKeyDown}
      onDragLeave={handleContainerDragLeave}>
      <WorkListComp>
        <Space />
        {isOverviewOpenWithIndexing && (
          <WorkViewerShell
            onDragEnter={handleViewerDragEnter}
            onDragOver={(e) => handleDragOver(
              e,
              isEditMode && !isIndexingMode && !hasMultiSelected,
            )}
            onDrop={(e) => handleDrop(e, 'viewer')}
          >
            {isIndexingMode ? <IndexWorkViewer />
              : hasMultiSelected ? <MultiWorkViewer />
              : <WorkViewer />}
          </WorkViewerShell>
        )}
      <Space2 />
      </WorkListComp>
      <ListRail>
        <WorkViewerInfo />
        <WorkInfoListContainer>
          <WorkMetaStation />
          <SideGridHost
            ref={sideGridHostRef}
            $heightPx={listHeightPx}
            $heightReady={listHeightReady}
            $minVh={sideGridMinVh}
            $maxVh={sideGridMaxVh}
            $marqueeSelecting={marqueeSelecting}
            onMouseDown={handleWorkListMouseDown}
            onClickCapture={handleWorkListClickCapture}
            onClick={handleWorkListBackgroundClick}
            onDragOver={(e) => handleDragOver(e, isEditMode && !isIndexingMode)}
            onDrop={(e) => handleDrop(e, 'list')}
            onDragEnter={handleListDragEnter}
          >
            {visibleWorkList.length > 0 && (
              <WorkGrid ref={sideGridRef} $cols={gridCols}>
                {visibleWorkList.map((each: WorkData, i) =>
                  <WorkInfo
                    key={each.id}
                    data={each}
                    index={(page.page - 1) * perPageToNumber(pageSize) + i + 1}
                    handleWorkClick={handleWorkClick}
                  />
                )}
              </WorkGrid>
            )}
            {canShowList && visibleWorkList.length === 0 && (
              <EmptySlot $minVh={sideGridMinVh}>
                <EmptyWorkList />
              </EmptySlot>
            )}
            {marqueeBox && (
              <MarqueeSelectionBox
                style={{
                  left: marqueeBox.left,
                  top: marqueeBox.top,
                  width: marqueeBox.width,
                  height: marqueeBox.height,
                }}
              />
            )}
          </SideGridHost>
          {editButtons}
          <PageAboutWork />
        </WorkInfoListContainer>
      </ListRail>
      <DragDropLayer dragState={dragState} />
    </WorkListContainer>
  );
}

const WorkListContainer = styled.article`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  outline: none;
  position: relative;
`;

const WorkListComp = styled.section`
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  position: relative;
  overflow: hidden;
`;

const WorkGrid = styled.div<{ $cols: number }>`
  --work-grid-basis: 100cqi;
  --work-grid-col-gap-prev: calc((var(--work-grid-basis) - 5 * ((var(--work-grid-basis) - 60px) / 6)) / 4 * 2 / 3);
  --work-grid-col-width: calc((var(--work-grid-basis) - 6 * var(--work-grid-col-gap-prev)) / 7);
  --work-grid-col-gap: calc((var(--work-grid-basis) - 5 * var(--work-grid-col-width)) / 4 * 2 / 3);
  --work-grid-row-gap: var(--work-grid-col-gap);
  flex: 0 0 auto;
  width: max-content;
  max-width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols}, minmax(0, var(--work-grid-col-width)));
  grid-auto-rows: min-content;
  align-content: start;
  align-items: stretch;
  row-gap: var(--work-grid-row-gap);
  column-gap: var(--work-grid-col-gap);
`;

const ListRail = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  min-height: 0;
  flex-shrink: 0;
`;

const Space = styled.div`
  flex: 10;
  min-width: 0;
  min-height: 0;
`;
const Space2 = styled.div`
  /* 접힌 상태의 Space:Space2 비율(10:1)을 토글 후에도 유지 */
  flex: 0 0 max(0px, calc((100% - min(50vw, 700px)) / 11));
  min-width: 28px;
  min-height: 0;
`;

const WorkInfoListContainer = styled.div`
  width: clamp(400px, 32vw, 550px);
  display: flex;
  flex-direction: column;
  position: relative;
  margin-top: auto;
  margin-bottom: 6vh;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const SideGridHost = styled.div<{
  $heightPx: number | null;
  $heightReady: boolean;
  $minVh: number;
  $maxVh: number;
  $marqueeSelecting?: boolean;
}>`
  flex-shrink: 0;
  height: ${({ $heightPx }) => ($heightPx == null ? 'fit-content' : `${$heightPx}px`)};
  min-height: ${({ $heightPx, $minVh }) => ($heightPx == null ? `${$minVh}vh` : 0)};
  max-height: ${({ $heightPx, $maxVh }) => ($heightPx == null ? `${$maxVh}vh` : 'none')};
  overflow-x: hidden;
  overflow-y: auto;
  padding: 4px 0 0px 18px;
  box-sizing: border-box;
  container-type: inline-size;
  position: relative;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  transition: ${({ $heightReady }) => ($heightReady ? 'height 0.5s ease-in-out' : 'none')};
  user-select: ${({ $marqueeSelecting }) => ($marqueeSelecting ? 'none' : 'auto')};
  &::-webkit-scrollbar {
    display: none;
  }
`;

const EmptySlot = styled.div<{ $minVh: number }>`
  width: 100%;
  height: ${({ $minVh }) => `${$minVh}vh`};
  display: flex;
`;

const MarqueeSelectionBox = styled.div`
  position: absolute;
  pointer-events: none;
  box-sizing: border-box;
  border: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
  background: transparent;
  backdrop-filter: blur(1px);
  z-index: 3;
`;

const BtnContainer = styled.div`
  position: fixed;
  bottom: clamp(25px, 5vh, 50px);
  right: clamp(50px, 8vw, 70px);
  width: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  z-index: 4;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

export default WorkList;
